import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ambudocs_token";
const AUTH_PATHS = ["/auth/login", "/auth/refresh", "/auth/demo", "/auth/logout"];

import { isCacheable, putCache, getCache, isNetworkError, setOffline } from "@/lib/offlineCache";
export const api = axios.create({ baseURL: API, withCredentials: true });

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

const tokenExp = (t) => {
  try { return JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).exp * 1000; } catch (_) { return 0; }
};

// Renouvellement silencieux (un seul appel en vol) via le cookie HttpOnly de session.
let refreshing = null;
export function refreshSession() {
  if (!refreshing) {
    refreshing = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .then((r) => { setToken(r.data.access_token); return r.data; })
      .catch((e) => { if (e.response?.status === 401) setToken(null); throw e; })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

const isAuthPath = (url = "") => AUTH_PATHS.some((p) => url.includes(p));

api.interceptors.request.use(async (config) => {
  let token = getToken();
  // Jeton expiré ou sur le point d'expirer : on le renouvelle avant l'appel pour éviter tout 401 visible.
  if (token && !isAuthPath(config.url) && tokenExp(token) - Date.now() < 60_000) {
    try { token = (await refreshSession()).access_token; } catch (_) { token = getToken(); }
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use((r) => {
  if (isCacheable(r.config)) putCache(r.config, r.data);
  // Toute action réussie (POST/PATCH/PUT/DELETE) rafraîchit les compteurs du menu immédiatement.
  if (r.config && !/^get$/i.test(r.config.method || "get")) window.dispatchEvent(new Event("sanitdocs:refresh-counters"));
  return r;
}, async (err) => {
  const { response, config } = err;
  // Réseau faible / hors ligne : on sert la dernière donnée connue pour les GET chauffeur (pas d'écran vide).
  if (config && isNetworkError(err) && isCacheable(config)) {
    setOffline(true);
    const hit = await getCache(config);
    if (hit) return { data: hit.data, status: 200, statusText: "OK (cache)", headers: {}, config, fromCache: true };
  }
  if (response?.status === 423) {
    import("sonner").then(({ toast }) => toast.error(response.data?.detail || "Application en lecture seule"));
  }
  if (response?.status === 401 && config && !config._retried && !isAuthPath(config.url)) {
    config._retried = true;
    try {
      const { access_token } = await refreshSession();
      config.headers.Authorization = `Bearer ${access_token}`;
      return api(config);
    } catch (_) {
      window.dispatchEvent(new CustomEvent("sanitdocs:session-expired"));
    }
  }
  return Promise.reject(err);
});

export function apiError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}
